import classNames from 'classnames'
import { FC, PropsWithChildren } from 'react'

import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { handleAlgoliaClickEvent } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { getLegacyProductCardColorVariantsForAnalytics } from '@src/quince-site-common/src/utils/product-card-color-variant-analytics'
import { FindingMethodType, setFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import {
  clearCurrentRecommendationTabData,
  recommendationTabDataFromTrackContext,
  setCurrentRecommendationTabData,
} from '@src/telemetry/recommendation-tab-data'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { Link } from '../../../atoms'
import { TrackConfig } from '../../../hocs/withTracking'
import { useProductCardContext } from '../product-card-context/product-card.context'
import {
  getColorSwatchPosition,
  getIndividualTagStatus,
  getProductLink,
  getProductLinkWithSelectedColor,
  getProductTagsList,
} from '../productCard.helpers'

import styles from './product-card-link.module.scss'

export interface ProductCardLinkProps {
  selectorKey?: string
  tracker?: string
  additionalAttributes?: Record<string, string>
  productClickTrackConfig?: TrackConfig
  productCardLinkClass?: string
}

export const ProductCardLink: FC<PropsWithChildren<ProductCardLinkProps>> = props => {
  const { product, selectedOptions, siteSearchQuery, productPosition, productSearchObjectId } = useProductCardContext()
  const searchAlgoliaQueryId =
    useAppSelector<SearchDetails>(siteStateManager, siteStateManager.getSearchDetails).searchAlgoliaQueryId || ''
  // Control behavior: links open in same tab
  const hrefOverride = getProductLink(
    product,
    selectedOptions,
    props.tracker,
    props.additionalAttributes,
    siteSearchQuery,
    productPosition,
  )
  const href = hrefOverride || getProductLinkWithSelectedColor(product, selectedOptions, true)

  return (
    <Link
      selectorKey={props.selectorKey}
      className={classNames(styles.productLink, props.productCardLinkClass)}
      href={href}
      enableEarlyFetch={false}
      onClick={() => {
        handleAlgoliaClickEvent({
          objectID: productSearchObjectId,
          position: productPosition,
          queryID: searchAlgoliaQueryId,
        })
        if (props.productClickTrackConfig) {
          const productTags = getProductTagsList(product.tags, selectedOptions)
          const productClickTrackConfigContext = props.productClickTrackConfig?.context
          const selectedColor = selectedOptions.find(option => option.name == ProductOptionName.Color)?.value
          const colorVariantValues = getLegacyProductCardColorVariantsForAnalytics(product as Product)
          setFindingMethod[productClickTrackConfigContext?.findingMethod as FindingMethodType]?.({
            elementName: (productClickTrackConfigContext?.contentContainerName as string) || '',
            elementId: (productClickTrackConfigContext?.trackingId as string) || '',
          })
          const recTab = recommendationTabDataFromTrackContext(productClickTrackConfigContext)
          if (Object.keys(recTab).length > 0) {
            setCurrentRecommendationTabData(recTab)
          } else {
            clearCurrentRecommendationTabData()
          }
          trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
            category: props.productClickTrackConfig.category,
            action: props.productClickTrackConfig.action,
            label: props.productClickTrackConfig.label,
            value: props.productClickTrackConfig.value,
            context: {
              ...props.productClickTrackConfig.context,
              product_id: product.spid,
              color: selectedColor,
              color_swatch_position: getColorSwatchPosition(selectedColor, colorVariantValues),
              contentContainerType:
                props.productClickTrackConfig.context?.contentContainerType ||
                CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
              name: product.title,
              color_variant: colorVariantValues,
              count_color_variant: colorVariantValues?.length ?? 0,
              rating: product.productReviewSummary?.averageRating,
              product_position: props.productClickTrackConfig.context?.product_position,
              tags: productTags,
              tag_ids: transformTagsIntoAnalyticsTags(productTags),
              ...getIndividualTagStatus(productTags),
              productProperties: getProductPropertiesForTracking(product as Product),
            },
          })
        }
      }}
    >
      {props.children}
    </Link>
  )
}
